/**
 * DispatchCategoriesPanel — Settings → Dispatch. Operator-managed list of
 * the day blocks a dispatcher can drop on the board (PTO, Sick, Admin,
 * Site Visit, Warranty…). Persisted on the settings singleton as
 * `dispatch_categories`; consumed by the dispatch board, the tech board
 * and the time-off menu/modal via lib/dispatchCategories.js.
 */
import { useEffect, useState } from "react";
import { tr } from "@/i18n";
import { Plus, Trash2, GripVertical, RotateCcw, Truck, Save, Loader2 } from "lucide-react";
import { btn } from "@/components/common/btn";
import { SettingsPanel, CollapsibleSection, Callout, LoadingState } from "@/components/settings/SettingsUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getSettings, updateSettings } from "@/lib/api";
import { DEFAULT_DISPATCH_CATEGORIES, CATEGORY_KINDS, normalizeCategories, refreshDispatchCategories } from "@/lib/dispatchCategories";

function slug(label) {
  return String(label).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

export default function DispatchCategoriesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings()
      .then(r => setRows(normalizeCategories(r.data?.dispatch_categories)))
      .catch(() => setRows(DEFAULT_DISPATCH_CATEGORIES))
      .finally(() => setLoading(false));
  }, []);

  const patch = (idx, field, value) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const remove = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
  const move = (idx, dir) => setRows(prev => {
    const next = [...prev];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const add = () => setRows(prev => [...prev, {
    id: `cat_${Date.now()}`,
    label: "",
    kind: "unavailable",
    color: CATEGORY_KINDS.unavailable.color,
    blocks_dispatch: false,
    capture_details: true,
  }]);

  const save = async () => {
    const cleaned = rows.map(r => ({ ...r, label: String(r.label || "").trim() }));
    if (cleaned.some(r => !r.label)) { toast.error("Every category needs a name"); return; }
    const labels = cleaned.map(r => r.label.toLowerCase());
    if (new Set(labels).size !== labels.length) { toast.error("Category names must be unique"); return; }
    setSaving(true);
    try {
      const payload = cleaned.map(r => ({
        id: r.id || slug(r.label),
        label: r.label,
        kind: r.kind,
        color: CATEGORY_KINDS[r.kind]?.color || r.color,
        blocks_dispatch: r.blocks_dispatch !== false,
        capture_details: !!r.capture_details,
      }));
      const res = await updateSettings({ dispatch_categories: payload });
      const next = normalizeCategories(res.data?.dispatch_categories);
      setRows(next);
      refreshDispatchCategories(next);
      toast.success("Dispatch categories saved");
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SettingsPanel><LoadingState testId="dispatch-categories-loading" /></SettingsPanel>;

  return (
    <CollapsibleSection
      id="dispatch-categories" testId="dispatch-categories-panel"
      icon={Truck} iconClass="text-amber-600"
      title="Dispatch categories"
      extra={<span className="text-[10px] px-1.5 py-0.5 rounded-full border font-semibold bg-slate-100 text-slate-600 border-slate-200">{rows.length}</span>}
      hint="Day blocks a dispatcher can drop on the board"
      actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setRows(DEFAULT_DISPATCH_CATEGORIES)} data-testid="dispatch-categories-reset">
              <RotateCcw className="w-3.5 h-3.5 mr-1" />Reset to defaults
            </Button>
            <button type="button" className={`${btn("write")} w-8 justify-center !px-0`} onClick={save} disabled={saving}
              title={saving ? "Saving…" : "Save"} aria-label="Save Dispatch Categories" data-testid="dispatch-categories-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </button>
          </>
        }
    >
      <div className="space-y-3">
        <Callout>
          These are the blocks a dispatcher can drop on a tech's day (double-click any empty cell on the
          dispatch board). <span className="font-semibold" style={{ color: CATEGORY_KINDS.off.color }}>{tr("Off")}</span> (red)
          means the tech is not working — automations and notifications to them pause.{" "}
          <span className="font-semibold" style={{ color: CATEGORY_KINDS.unavailable.color }}>Unavailable</span> (orange)
          means on the clock but not schedulable for service work.{" "}
          <span className="font-semibold">Blocks dispatching</span> means no work order can be
          scheduled on that day. <span className="font-semibold">Hours + notes</span> asks for the detail the
          tech sees on their own schedule.
        </Callout>

        <div className="space-y-2">
          {rows.map((r, idx) => (
            <div key={r.id || idx} className="flex items-center gap-2 p-2 rounded-md border border-slate-200 bg-white" data-testid={`dispatch-category-row-${idx}`}>
              <div className="flex flex-col text-slate-300">
                <button type="button" onClick={() => move(idx, -1)} className="hover:text-slate-600 leading-none text-[10px]" aria-label="Move Up" data-testid={`dispatch-category-up-${idx}`}>▲</button>
                <button type="button" onClick={() => move(idx, 1)} className="hover:text-slate-600 leading-none text-[10px]" aria-label="Move Down" data-testid={`dispatch-category-down-${idx}`}>▼</button>
              </div>
              <GripVertical className="w-3.5 h-3.5 text-slate-300 shrink-0" />
              <div className="flex rounded-md border border-slate-200 overflow-hidden shrink-0" data-testid={`dispatch-category-kind-${idx}`}>
                {Object.entries(CATEGORY_KINDS).map(([k, meta]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => patch(idx, "kind", k)}
                    title={meta.hint}
                    className={`px-2 h-8 text-[11px] font-semibold first:border-r border-slate-200 ${r.kind === k ? "text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                    style={r.kind === k ? { backgroundColor: meta.color } : undefined}
                    data-testid={`dispatch-category-kind-${k}-${idx}`}
                  >
                    {meta.label}
                  </button>
                ))}
              </div>
              <Input
                value={r.label}
                onChange={(e) => patch(idx, "label", e.target.value)}
                placeholder="Category name (e.g. Site Visit)"
                className="h-9 flex-1 min-w-[140px]"
                data-testid={`dispatch-category-label-${idx}`}
              />
              <span
                className="cat-block hidden sm:inline-block rounded px-2 py-1 text-[10px] font-bold uppercase tracking-wide"
                style={{ "--cat": CATEGORY_KINDS[r.kind]?.color || r.color }}
                data-testid={`dispatch-category-preview-${idx}`}
              >
                {r.label || "Preview"}
              </span>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={r.blocks_dispatch !== false}
                  onChange={(e) => patch(idx, "blocks_dispatch", e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-300"
                  data-testid={`dispatch-category-blocks-${idx}`}
                />
                Blocks dispatching
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!r.capture_details}
                  onChange={(e) => patch(idx, "capture_details", e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-300"
                  data-testid={`dispatch-category-details-${idx}`}
                />
                Hours + notes
              </label>
              <Button variant="outline" size="sm" onClick={() => remove(idx)} className="text-rose-600 hover:bg-rose-50 shrink-0" data-testid={`dispatch-category-delete-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>

        <Button variant="outline" size="sm" onClick={add} data-testid="dispatch-category-add">
          <Plus className="w-3.5 h-3.5 mr-1" />Add category
        </Button>
      </div>
    </CollapsibleSection>
  );
}
